import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, adminLogin, adminLogout, adminMe } from "../lib/api";
import { useI18n } from "../i18n";
import { themeStyle } from "../lib/theme";
import { LangToggle } from "../components/LangToggle";
import { Cake, LogOut } from "../components/Icon";
import { StatsTab } from "../components/admin/StatsTab";
import { ReservationsTab } from "../components/admin/ReservationsTab";
import { WallTab } from "../components/admin/WallTab";
import { ToolsTab } from "../components/admin/ToolsTab";

type Tab = "stats" | "reservations" | "wall" | "tools";

export function Admin() {
  const { t } = useI18n();
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>("stats");

  useEffect(() => {
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) return null;

  return (
    <div style={themeStyle("brand")} className="theme-wash min-h-dvh">
      <header className="sticky top-0 z-20 border-b border-black/[0.06] bg-cream/85 backdrop-blur">
        <div className="container-x flex items-center gap-3 py-3">
          <Link to="/" className="inline-flex items-center gap-2 font-display text-lg font-bold text-[var(--theme-deep)]">
            <Cake className="h-5 w-5" /> Admin
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <LangToggle />
            {authed && (
              <button
                onClick={() => adminLogout().then(() => setAuthed(false))}
                className="btn-ghost px-4 py-2 text-sm"
              >
                <LogOut className="h-4 w-4" /> {t("admin.logout")}
              </button>
            )}
          </div>
        </div>
      </header>

      {!authed ? (
        <Login onOk={() => setAuthed(true)} />
      ) : (
        <div className="container-x py-6">
          <nav className="mb-6 flex flex-wrap gap-1 rounded-full bg-white/70 p-1 ring-1 ring-black/5">
            {(
              [
                ["stats", t("admin.tabStats")],
                ["reservations", t("admin.tabReservations")],
                ["wall", t("admin.tabWall")],
                ["tools", t("admin.tabTools")],
              ] as [Tab, string][]
            ).map(([v, label]) => (
              <button
                key={v}
                onClick={() => setTab(v)}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${
                  tab === v ? "bg-[var(--theme-main)] text-white" : "text-ink/60 hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>

          {tab === "stats" && <StatsTab />}
          {tab === "reservations" && <ReservationsTab />}
          {tab === "wall" && <WallTab />}
          {tab === "tools" && <ToolsTab />}
        </div>
      )}
    </div>
  );
}

function Login({ onOk }: { onOk: () => void }) {
  const { t } = useI18n();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await adminLogin(pw);
      onOk();
    } catch (e2) {
      setErr(e2 instanceof ApiError && e2.code === "bad_password" ? t("admin.badPassword") : t("reserve.errors.generic"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container-x flex justify-center py-20">
      <form onSubmit={submit} className="card w-full max-w-sm">
        <h1 className="section-title text-center">{t("admin.title")}</h1>
        <label className="field-label mt-6">{t("admin.password")}</label>
        <input type="password" className="field" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
        {err && <p className="mt-2 text-center text-sm text-red-500">{err}</p>}
        <button type="submit" disabled={busy} className="btn-primary mt-4 w-full">
          {busy ? t("admin.loggingIn") : t("admin.login")}
        </button>
      </form>
    </div>
  );
}
